/**
 * 后端API基础URL配置
 * 根据当前访问的域名和环境自动选择对应的后端地址
 * 
 * 支持两种配置策略：
 * 1. 基于端口判断（适用于后端API，如队列管理）
 * 2. 基于生产域名判断（适用于外部系统，如标签管理）
 */

/**
 * 获取后端API基础URL（基于端口判断策略）
 * 适用于后端API服务，根据当前访问的域名和端口自动判断后端地址
 * 
 * @param {Object} options - 配置选项
 * @param {string} options.defaultPort - 默认后端端口（默认：3210）
 * @param {string} options.envVarName - 环境变量名称（默认：VUE_APP_API_BASE_URL）
 * @returns {string} 后端API基础URL
 */
export function getBackendBaseUrl(options = {}) {
  const {
    defaultPort = '3210',
    envVarName = 'VUE_APP_API_BASE_URL'
  } = options;

  // 优先使用环境变量配置（Vue CLI 会自动加载 .env 文件中的 VUE_APP_ 开头的变量）
  const envApiUrl = process.env[envVarName];
  if (envApiUrl) {
    console.log(`[BackendUrl] 使用环境变量 ${envVarName} 配置的后端地址:`, envApiUrl);
    return envApiUrl;
  }

  // 开发环境：后端运行在指定端口
  if (process.env.NODE_ENV === 'development') {
    const devUrl = `http://localhost:${defaultPort}`;
    console.log('[BackendUrl] 开发环境，使用:', devUrl);
    return devUrl;
  }

  // 测试/生产环境：根据当前域名判断
  if (typeof window !== 'undefined' && window.location) {
    const currentHostname = window.location.hostname;
    const currentProtocol = window.location.protocol;
    const currentPort = window.location.port;

    console.log('[BackendUrl] 当前访问信息:', {
      hostname: currentHostname,
      protocol: currentProtocol,
      port: currentPort,
      origin: window.location.origin,
      nodeEnv: process.env.NODE_ENV
    });

    // 如果是 localhost 或 127.0.0.1，使用指定端口（本地开发或测试）
    if (currentHostname === 'localhost' || currentHostname === '127.0.0.1') {
      const url = `${currentProtocol}//${currentHostname}:${defaultPort}`;
      console.log('[BackendUrl] 本地访问，使用:', url);
      return url;
    }

    // 测试环境：如果前端和后端在同一主机但不同端口
    // 尝试使用当前主机名 + 指定端口
    // 如果前后端同域部署（同一端口），使用当前域名
    if (currentPort && currentPort !== defaultPort && currentPort !== '80' && currentPort !== '443') {
      // 前端和后端在不同端口，使用当前主机名 + 指定端口
      const url = `${currentProtocol}//${currentHostname}:${defaultPort}`;
      console.log(`[BackendUrl] 测试环境（不同端口），使用:`, url);
      return url;
    }

    // 其他情况：假设前后端同域部署，使用当前域名
    console.log('[BackendUrl] 同域部署，使用当前域名:', window.location.origin);
    return window.location.origin;
  }

  // 默认fallback
  console.warn('[BackendUrl] 无法获取 window.location，使用默认值');
  return window.location.origin;
}

/**
 * 获取外部系统基础URL（基于生产域名判断策略）
 * 适用于外部系统，根据当前访问的域名判断使用生产环境还是测试环境URL
 * 
 * @param {Object} options - 配置选项
 * @param {string} options.envVarName - 环境变量名称
 * @param {string} options.productionUrl - 生产环境URL
 * @param {string} options.developmentUrl - 开发/测试环境URL
 * @param {string} options.productionDomain - 生产环境域名（用于判断）
 * @returns {string} 外部系统基础URL
 */
export function getExternalSystemBaseUrl(options = {}) {
  const {
    envVarName,
    productionUrl,
    developmentUrl,
    productionDomain
  } = options;

  if (!envVarName || !productionUrl || !developmentUrl || !productionDomain) {
    console.error('[BackendUrl] getExternalSystemBaseUrl 缺少必需参数');
    return developmentUrl || '';
  }

  // 优先使用环境变量配置
  const envUrl = process.env[envVarName];
  if (envUrl) {
    console.log(`[BackendUrl] 使用环境变量 ${envVarName} 配置的URL:`, envUrl);
    return envUrl;
  }

  // 根据当前访问的域名判断
  if (typeof window !== 'undefined' && window.location) {
    const currentHostname = window.location.hostname;

    // 如果是生产环境域名，使用生产URL
    if (currentHostname === productionDomain) {
      console.log(`[BackendUrl] 生产环境域名 (${productionDomain})，使用生产URL:`, productionUrl);
      return productionUrl;
    }
  }

  // 默认使用测试环境URL
  console.log('[BackendUrl] 使用测试环境URL:', developmentUrl);
  return developmentUrl;
}

/**
 * 获取后端API基础URL（默认配置）
 * 用于队列管理等需要访问后端API的场景
 */
export const BACKEND_BASE_URL = getBackendBaseUrl();

/**
 * 获取后端配置信息，方便调试
 */
export function getBackendConfig() {
  const currentHostname = typeof window !== 'undefined' && window.location
    ? window.location.hostname
    : 'unknown';
  
  const currentPort = typeof window !== 'undefined' && window.location
    ? window.location.port
    : 'unknown';

  return {
    baseUrl: BACKEND_BASE_URL,
    hostname: currentHostname,
    port: currentPort,
    origin: typeof window !== 'undefined' && window.location
      ? window.location.origin
      : 'unknown',
    hasEnvVar: !!process.env.VUE_APP_API_BASE_URL,
    env: process.env.NODE_ENV || 'unknown',
    envVarValue: process.env.VUE_APP_API_BASE_URL || null
  };
}

// 导出配置信息，方便调试
export const BACKEND_CONFIG = getBackendConfig();
